<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <admin-sub-menu v-if="userProfile.sysAdmin === 'Y'" />
        <br>
        <div class="columns is-multiline is-vcentered has-less-padding">
          <div class="column is-4">
            <textbox
              v-model="searchString"
              placeholder="Search by username, first, and/or last name"
              name="search"
              type="text"
              class="cell no-margin"
            />
          </div>
          <div class="column is-3">
            <v-selectmenu
              v-model="selectedAgencies"
              key-field="id"
              show-field="description"
              :data="agencies"
              :language="'en'"
              :multiple="true"
              :full-width="true"
              :query="true"
              :title="'Select Roles'"
            >
              <button
                id="depts-dropdown-btn"
                type="button"
                class="sm-default-btn"
              >
                <div>
                  <strong>Departments</strong>{{ selectedAgencies !== '' ? ': ' + getDropdownLabel(selectedAgencies, 'agencies') : '' }}
                </div>
              </button>
            </v-selectmenu>
          </div>
          <div class="column is-5">
            <checkbox-adv
              v-model="includeInactive"
              :value="true"
              class="no-margin"
            >
              Include inactive filers
            </checkbox-adv>
          </div>
          <div class="column is-5">
            <v-selectmenu
              v-model="selectedUserAccess"
              key-field="id"
              show-field="description"
              :data="userAccess"
              :language="'en'"
              :multiple="true"
              :full-width="true"
              :query="false"
              :title="'Select Roles'"
            >
              <button
                id="roles-dropdown-btn"
                type="button"
                class="sm-default-btn"
              >
                <div>
                  <strong>Roles</strong>{{ selectedUserAccess !== '' ? ': ' + getDropdownLabel(selectedUserAccess, 'userAccess') : '' }}
                </div>
              </button>
            </v-selectmenu>
          </div>
          <div class="column is-4">
            <v-selectmenu
              v-model="selectedStatements"
              key-field="id"
              show-field="description"
              :data="requiredStatements"
              :language="'en'"
              :multiple="true"
              :full-width="true"
              :query="false"
              :title="'Select Statements'"
            >
              <button
                id="statements-dropdown-btn"
                type="button"
                class="sm-default-btn"
              >
                <div>
                  <strong>Statements</strong>{{ selectedStatements !== '' ? ': ' + getDropdownLabel(selectedStatements, 'requiredStatements') : '' }}
                </div>
              </button>
            </v-selectmenu>
          </div>
          <div class="column is-3">
            <button
              id="search-btn"
              class="button is-secondary"
              type="submit"
              @click="filterUsers"
            >
              <span>Search</span>
              <span class="icon is-small">
                <i class="fas fa-search" />
              </span>
            </button>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <div class="cell">
              <div
                id="filers-table"
                class="table-responsive"
              >
                <div class="legends">
                  &nbsp;
                  <span class="icon has-text-danger is-small">
                    <i class="fad fa-user-circle" />
                  </span> Inactive &nbsp;
                  <span class="icon has-text-electric-blue is-small">
                    <i class="fad fa-user-circle" />
                  </span> System Admin &nbsp;
                  <span class="icon has-text-phanatic-green is-small">
                    <i class="fad fa-user-circle" />
                  </span> Department Admin &nbsp;
                </div>
                <v-server-table
                  ref="allUsers"
                  :columns="tableColumns"
                  :options="tableOptions"
                >
                  <template
                    slot="userName"
                    slot-scope="props"
                  >
                    <div class="legend-icons">
                      <span
                        v-if="props.row.active !== 'Y'"
                        class="icon has-text-danger is-small"
                      >
                        <i class="fad fa-xs fa-user-circle" />
                      </span>
                      <span
                        v-if="props.row.sysAdmin === 'Y'"
                        class="icon has-text-electric-blue is-small"
                      >
                        <i class="fad fa-xs fa-user-circle" />
                      </span>
                      <span
                        v-if="props.row.deptHead === 'Y'"
                        class="icon has-text-phanatic-green is-small"
                      >
                        <i class="fad fa-xs fa-user-circle" />
                      </span>
                    </div>
                    {{ props.row.userName }}
                  </template>
                  <template
                    slot="primaryPosition"
                    slot-scope="props"
                  >
                    <template v-if="props.row.primaryAgencyId">
                      {{ getControlValueDesc(props.row.primaryAgencyId, 'agencies') }}
                    </template>
                  </template>
                  <template
                    slot="requiredStatements"
                    slot-scope="props"
                  >
                    <div class="columns has-text-centered">
                      <div
                        :class="{ disabled: props.row.stateForm !== 'Y' }"
                        class="column"
                      >
                        <i
                          v-show="props.row.stateForm === 'Y'"
                          class="far fa-check-square"
                        />
                        <i
                          v-show="props.row.stateForm !== 'Y'"
                          class="far fa-square"
                        />
                      </div>
                      <div
                        :class="{ disabled: props.row.cityForm !== 'Y' }"
                        class="column"
                      >
                        <i
                          v-show="props.row.cityForm === 'Y'"
                          class="far fa-check-square"
                        />
                        <i
                          v-show="props.row.cityForm !== 'Y'"
                          class="far fa-square"
                        />
                      </div>
                      <div
                        :class="{ disabled: props.row.mayorForm !== 'Y' }"
                        class="column"
                      >
                        <i
                          v-show="props.row.mayorForm === 'Y'"
                          class="far fa-check-square"
                        />
                        <i
                          v-show="props.row.mayorForm !== 'Y'"
                          class="far fa-square"
                        />
                      </div>
                    </div>
                  </template>

                  <template
                    slot="editFiler"
                    slot-scope="props"
                  >
                    <a
                      :title="`Edit ${props.row.firstName} ${props.row.lastName} profile`"
                      :aria-label="`Edit ${props.row.firstName} ${props.row.lastName} profile`"
                      href="#"
                      @click.prevent="editFiler(props.row)"
                    >
                      <span class="icon is-small">
                        <i class="fas fa-user-edit" />
                      </span>
                    </a>
                  </template>
                  <template
                    slot="userStatements"
                    slot-scope="props"
                  >
                    <a
                      :title="`User ${props.row.firstName} ${props.row.lastName} statements`"
                      :aria-label="`User ${props.row.firstName} ${props.row.lastName} statements`"
                      href="#"
                      @click.prevent="viewUserStatements(props.row)"
                    >
                      <span class="icon is-small">
                        <i class="fas fa-file-alt" />
                      </span>
                    </a>
                  </template>
                  <template
                    slot="createdOn"
                    slot-scope="props"
                  >
                    {{ formatDate(props.row.createdOn, 'MM/DD/YYYY') }}
                  </template>
                </v-server-table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import AdminSubMenu from "@/components/AdminSubMenu";
import { controlValueHelpers } from '@/mixins/controlValue';
import EditUserProfile from '@/modals/EditUserProfile';
import UserStatements from '@/modals/UserStatements';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
import { Textbox } from '@phila/phila-ui';
import UploadStatements from '@/modals/UploadStatements';
import { ServerTable } from 'vue-tables-2';
import vSelectMenu from 'v-selectmenu';
Vue.use(ServerTable, {}, false, 'bulma');

Vue.use(vSelectMenu);


export default {
  components: {
    AdminSubMenu,
    CheckboxAdv,
    Textbox,
  },
  mixins: [
    controlValueHelpers,
  ],
  data() {
    return {
      agencies: [],
      requiredStatements: [
        {
          id: 'city',
          description: 'City',
        },
        {
          id: 'state',
          description: 'State',
        },
        {
          id: 'mayor',
          description: 'Mayor',
        },
      ],
      includeInactive: false,
      selectedAgencies: '',
      selectedStatements: '',
      selectedUserAccess: 'filer',
      searchString: '',
      filteredUsers: [],
      allUsers: [],
      filter: {
        cityForm: false,
        mayorForm: false,
        stateForm: false,
        isSysAdmin: false,
        isDeptHead: false,
        isFiler: true,
      },
      tableColumns: [
        "userName",
        "firstName",
        "lastName",
        "primaryPosition",
        "requiredStatements",
        "editFiler",
        "userStatements",
        "createdOn",
      ],
      tableOptions: {
        requestFunction: this.customRequestFunction,
        responseAdapter: this.customResponseAdapter,
        headings: {
          userName: "Username",
          firstName: "First Name",
          lastName: "Last Name",
          primaryPosition: "Department",
          userStatements: 'STMT',
          createdOn: "Created At",
          requiredStatements: function() {
            return <div>
                Required Statements
            <div class="columns">
              <div class="column">
                    State
              </div>
              <div class="column">
                    City
              </div>
              <div class="column">
                    Mayor
              </div>
            </div>
            </div>;
          },
          editFiler: "Edit",
        },
        columnsClasses: {
          id: 'text-center',
          requiredStatements: "has-text-centered",
          editFiler: "has-text-centered",
          userStatements: "has-text-centered",
        },
        sortIcon: {
          is: "fal fa-sort",
          up: "fad fa-sort-up",
          down: "fad fa-sort-down",
        },
        perPage: 20,
        perPageValues: [],
        sortable: [ "id", "lastName", "firstName" ],
        filterable: false,
        pagination: {
          edge: true,
          nav: 'scroll',
        },
      },
      userProfile: {},
      deptAccess: [],
    };
  },
  computed: {
    userAccess () {
      let roles = [
        {
          id: 'filer',
          description: 'Filers',
        },
      ];

      if (this.userProfile.sysAdmin === 'Y') {
        roles.push({
          id: 'sysAdmin',
          description: 'System Admins',
        },
        {
          id: 'deptHead',
          description: 'Department Heads',
        });
      }

      return roles;
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    getUserProfile () {
      return this.cloneObj(this.$store.state.user.profile);
    },
    getUserDeptsAccess () {
      return this.$store.state.user.deptAccess;
    },
    getAgencies () {
      if (this.userProfile.sysAdmin === 'Y') {
        return this.cloneObj(this.$store.getters[ 'admin/activeControlValues' ]('agency'));
      }
      let agencies = this.cloneObj(this.$store.getters[ 'admin/activeControlValues' ]('agency'));
      let allowedAgencies = agencies.filter(agency => {
        if (this.deptAccess.includes(agency.id)){
          return true;
        }
        return false;
      });
      return allowedAgencies;
    },
    getDropdownLabel (keys, variable) {
      const keysArray = keys.split(",");
      let labels = [];
      keysArray.forEach(key => {
        this[variable].forEach(item => {
          if (item.id == key) {
            labels.push(item.description);
          }
        });
      });
      return labels.join(', ');
    },
    async customRequestFunction (params) {
      const query = {
        ascending: params.ascending === 1 ? true : false,
        searchString: this.searchString,
        pageNumber: params.page,
        pageSize: params.limit,
        city: this.selectedStatements.search('city') >= 0 ? true : false,
        state: this.selectedStatements.search('state') >= 0 ? true : false,
        mayor: this.selectedStatements.search('mayor') >= 0 ? true : false,
        includeInactive: this.includeInactive,
        filer: this.selectedUserAccess.search('filer') >= 0 ? true : false,
        deptHead: this.selectedUserAccess.search('deptHead') >= 0 ? true : false,
        sysAdmin: this.selectedUserAccess.search('sysAdmin') >= 0 ? true : false,
        orderByCol: params.orderBy ? params.orderBy.charAt(0).toUpperCase() + params.orderBy.slice(1) : params.orderBy,
      };

      if (this.selectedAgencies !== '') {
        query.departmentIds = this.selectedAgencies.split(',').map(Number);
      }

      try {
        return await this.$store.dispatch('user/searchUsers', query);
      } catch (err) {
        return [];
      }
    },
    customResponseAdapter (data) {
      const resp = { data: [], count: 0 };
      if (data) {
        resp.data = data.userListResults;
        resp.count = data.totalCount;
      }
      return resp;
    },
    async initialAssign () {
      this.userProfile = await this.getUserProfile();
      this.deptAccess = await this.getUserDeptsAccess();
      this.agencies = await this.getAgencies();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'agency'));
      // promises.push(this.$store.dispatch('user/fetchUserProfile'));
      promises.push(this.$store.dispatch('user/fetchUserDepts'));
      return Promise.all(promises);
    },
    filterUsers () {
      this.$refs['allUsers'].refresh();
    },
    searchFilter (users) {
      return users.filter(user => {
        let firstName = user.firstName ? user.firstName.toLowerCase() : '';
        let lastName = user.lastName ? user.lastName.toLowerCase() : '';
        let searchString = this.searchString.toLowerCase();
        return firstName === searchString || lastName === searchString || `${firstName} ${lastName}` === searchString || (`${firstName}${lastName}`.search(searchString) !== -1);
      });
    },
    statementsFilter (users) {
      return users.filter(user => {
        return user[this.selectedStatement] === 'Y';
      });
    },
    departmentsFilter (users) {
      return users.filter(user => {
        return parseInt(user.primaryAgencyId) === parseInt(this.selectedAgency);
      });
    },
    editFiler(row) {
      this.$store.commit('user/setTempId', row.id);
      this.$modal.show(EditUserProfile, {}, {
        classes: [ 'is-large' ],
      });
    },
    viewUserStatements(row) {
      this.$store.commit('user/setTempId', row.id);
      this.$modal.show(UserStatements, {}, {
        classes: [ 'is-large' ],
      });
    },
    uploadStatements(row) {
      this.$modal.show(UploadStatements, {
        filer: row,
      },
      {
        classes: [ 'is-small' ],
      });
    },
  },
};
</script>
<style lang="scss">
  .v-dropdown-caller {
    width: 100%;
    .sm-caller-container {
      width: 100%;
      button.sm-default-btn {
        width: 100%;
        max-width: 100%;
        text-align: left;
        border-radius: 0;
        box-shadow: none;
        height: 40px;
        padding: 0 0 0 10px;
        vertical-align: middle;
        > div {
          max-width: 90%;
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 40px;
          &:before {
            content: " ";
            background: white;
            width: 30px;
            height: 100%;
            display: block;
            z-index: 9;
            top: 0;
            right: 0;
            position: absolute;
          }
          &:after {
            content: "";
            right: 1.125em;
            z-index: 10;
            border: 3px solid transparent;
            border-color: #0f4d8f;
            border-radius: 2px;
            border-right: 0;
            border-top: 0;
            display: block;
            height: 0.625em;
            margin-top: -0.4375em;
            pointer-events: none;
            position: absolute;
            top: 50%;
            transform-origin: center;
            width: 0.625em;
            transform: rotate(-45deg);
          }
        }
      }
    }
  }
  .table-checkboxes {
    .cell {
      line-height: 0;
    }
    .far, .fas, .fal {
      font-size: 20px;
      color: #0e4d90;
    }
    .disabled {
      .far, .fas, .fal {
        color: #a9a9a9;
      }
    }
  }
  .columns.has-less-padding {
    .column {
      padding: .4rem 0.75em ;
    }
  }
  .VueTables {
    td, th {
      vertical-align: middle;
      position: relative;
      overflow-wrap: break-word;
    }
    td:first-child {
      width: 10%;
      padding-left: 10px;
    }
    .legend-icons {
      position: absolute;
      top:2px;
      right:2px;
      width: auto;
      height: auto;
      text-align: right;
      line-height: 0;
      span {
        padding: 0;
        margin: 0;
      }
    }
  }
  .legends {
    margin-bottom: 2px;
  }
</style>
